import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LiveChatUser } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { isMobile } from "@scrile/tools/dist/lib/browserUtils";
import useBreakPoints from "hooks/useBreakPoints";
import useMounted from "hooks/useMounted";
import { useUserFirstDocInteract } from "hooks/useUserFirstDocInteract";
import { RightPanelView } from "./components/RightPanel";

interface Props {
  userList: LiveChatUser[];
  onClose: () => void;
  showConfirmationExit: boolean;
  hideChatOnStart?: boolean;
  chatInLive?: boolean;
}

interface DisplayControls {
  rightPanel: RightPanelView;
  rightPanelHidden: boolean;
  controls: boolean;
  confirmationExit: boolean;
  hasUnreadMessage: boolean;
  chatWasHidden: boolean;
  fullscreen: boolean;
  /** True while mouse has been idle ≥ FULLSCREEN_IDLE_MS in fullscreen mode.
   *  Hides controls + cursor for an immersive feel; flips back to false on
   *  any mousemove / keydown. Ignored outside fullscreen. */
  controlsIdle: boolean;
}

const FULLSCREEN_IDLE_MS = 3000;

// Webkit-prefixed fullscreen surface for older Safari. Modern Chrome /
// Firefox / Safari 16+ expose the standard names — we feature-detect both.
type FsElement = HTMLDivElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};
type FsDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

function getBrowserFullscreenElement(): Element | null {
  const d = document as FsDocument;
  return document.fullscreenElement || d.webkitFullscreenElement || null;
}

async function enterBrowserFullscreen(el: FsElement): Promise<boolean> {
  try {
    if (el.requestFullscreen) {
      await el.requestFullscreen();
      return true;
    }
    if (el.webkitRequestFullscreen) {
      await el.webkitRequestFullscreen();
      return true;
    }
  } catch {
    // User gesture missing, permission policy denies, or Safari quirk —
    // fall back to CSS overlay below.
  }
  return false;
}

async function exitBrowserFullscreen(): Promise<void> {
  const d = document as FsDocument;
  try {
    if (document.exitFullscreen && document.fullscreenElement) {
      await document.exitFullscreen();
    } else if (d.webkitExitFullscreen && d.webkitFullscreenElement) {
      await d.webkitExitFullscreen();
    }
  } catch {
    /* ignore */
  }
}

function useViewController({ userList, onClose, showConfirmationExit, hideChatOnStart, chatInLive }: Props) {
  const usersCount = userList.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  const { showStreamPlayButton } = useUserFirstDocInteract();
  const { desktopUp, mobileLandscape } = useBreakPoints();

  const [displayControls, setDisplayControls] = useState<DisplayControls>({
    rightPanel: RightPanelView.CHAT,
    rightPanelHidden: hideChatOnStart ?? (!chatInLive && !desktopUp),
    controls: false,
    confirmationExit: false,
    hasUnreadMessage: false,
    chatWasHidden: hideChatOnStart ?? (!chatInLive && !desktopUp),
    fullscreen: false,
    controlsIdle: false,
  });

  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fullscreenRootRef = useRef<HTMLDivElement | null>(null);
  const isMounted = useMounted();

  const showControlsButtons = useMemo(() => {
    if (!isMobile()) {
      return displayControls.controls || desktopUp;
    } else {
      return (
        (displayControls.controls || desktopUp) &&
        (chatInLive ? displayControls.rightPanel === RightPanelView.CHAT : displayControls.rightPanelHidden)
      );
    }
  }, [displayControls, desktopUp, chatInLive]);

  const changeRightPanel = (rightPanelView: RightPanelView) => {
    setDisplayControls((settings) => {
      let chatWasHidden = settings.chatWasHidden;
      if (
        rightPanelView === RightPanelView.HIDE_ALL &&
        settings.rightPanel !== RightPanelView.CHAT &&
        !settings.chatWasHidden
      ) {
        rightPanelView = RightPanelView.CHAT;
      }

      if (rightPanelView === RightPanelView.HIDE_ALL && settings.rightPanel === RightPanelView.CHAT) {
        chatWasHidden = true;
      }
      if (rightPanelView === RightPanelView.CHAT) {
        chatWasHidden = false;
      }

      return {
        ...settings,
        rightPanelHidden: rightPanelView === RightPanelView.HIDE_ALL,
        rightPanel: rightPanelView === RightPanelView.HIDE_ALL ? settings.rightPanel : rightPanelView,
        chatWasHidden,
      };
    });
  };

  const setHasUnreadMessage = useCallback(
    (v: boolean) => {
      if (v && !displayControls.rightPanelHidden) return;
      setDisplayControls((settings) => ({
        ...settings,
        hasUnreadMessage: v,
      }));
    },
    [displayControls.rightPanelHidden]
  );

  const onOpenChat = () => {
    setHasUnreadMessage(false);
    changeRightPanel(RightPanelView.CHAT);
  };

  const changeControls = (name: keyof DisplayControls, state: boolean) => {
    setDisplayControls((settings) => ({
      ...settings,
      [name]: state,
    }));
  };

  const onShowControls = () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }
    if (displayControls.controls) {
      changeControls("controls", false);
    } else {
      changeControls("controls", true);
      timeout.current = setTimeout(() => {
        if (isMounted.current) changeControls("controls", false);
      }, 3000);
    }
  };

  const onCloseEvent = async () => {
    changeControls("confirmationExit", false);
    await onClose();
  };

  const onClickEndCall = () => {
    if (showConfirmationExit) changeControls("confirmationExit", true);
    else onCloseEvent();
  };

  const setFullscreenState = useCallback((on: boolean) => {
    setDisplayControls((s) =>
      s.fullscreen === on ? s : { ...s, fullscreen: on, controlsIdle: false }
    );
  }, []);

  const toggleFullscreen = useCallback(async () => {
    if (isMobile()) return; // desktop-only by product decision
    const el = fullscreenRootRef.current as FsElement | null;
    if (!el) return;
    const currentlyOn = !!getBrowserFullscreenElement() || displayControls.fullscreen;
    if (!currentlyOn) {
      // Try the browser kiosk path first; if it fails (no gesture, blocked,
      // unsupported) we still flip the CSS overlay so the user gets _some_
      // fullscreen behaviour rather than nothing.
      await enterBrowserFullscreen(el);
      setFullscreenState(true);
    } else {
      await exitBrowserFullscreen();
      setFullscreenState(false);
    }
  }, [displayControls.fullscreen, setFullscreenState]);

  // Browser may exit fullscreen for reasons outside our toggle: user pressed
  // Esc while in browser-fullscreen, hit F11, switched tabs on some OS, etc.
  // Sync our CSS-overlay state with the browser truth.
  useEffect(() => {
    if (isMobile()) return;
    const sync = () => {
      const inBrowserFs = !!getBrowserFullscreenElement();
      setDisplayControls((s) => {
        // Only follow the browser DOWN — if user turned on CSS overlay
        // because browser API was unavailable, we don't want this to flip it.
        if (s.fullscreen && !inBrowserFs && document.hasFocus()) {
          return { ...s, fullscreen: false, controlsIdle: false };
        }
        return s;
      });
    };
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  // Keyboard shortcuts. Esc exits (only when we're driving the CSS overlay —
  // when browser is in real fullscreen the browser swallows Esc itself).
  // F toggles. Both ignored while typing in an input.
  useEffect(() => {
    if (isMobile()) return;
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }
      if (event.key === "Escape" && displayControls.fullscreen) {
        if (!getBrowserFullscreenElement()) {
          event.preventDefault();
          setFullscreenState(false);
        }
        return;
      }
      if (
        (event.key === "f" || event.key === "F") &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey
      ) {
        event.preventDefault();
        toggleFullscreen();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [displayControls.fullscreen, setFullscreenState, toggleFullscreen]);

  // Auto-hide controls + cursor when the mouse is idle in fullscreen. We
  // do not attach these listeners outside fullscreen — the existing hover
  // pattern handles control visibility there.
  useEffect(() => {
    if (!displayControls.fullscreen) {
      if (idleTimer.current) {
        clearTimeout(idleTimer.current);
        idleTimer.current = null;
      }
      return;
    }
    const setIdle = (next: boolean) => {
      setDisplayControls((s) => (s.controlsIdle === next ? s : { ...s, controlsIdle: next }));
    };
    const bump = () => {
      setIdle(false);
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        if (isMounted.current) setIdle(true);
      }, FULLSCREEN_IDLE_MS);
    };
    bump();
    window.addEventListener("mousemove", bump);
    window.addEventListener("keydown", bump);
    window.addEventListener("touchstart", bump);
    return () => {
      window.removeEventListener("mousemove", bump);
      window.removeEventListener("keydown", bump);
      window.removeEventListener("touchstart", bump);
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = null;
    };
  }, [displayControls.fullscreen, isMounted]);

  return {
    onClickEndCall,
    mobileLandscape,
    onShowControls,
    showControls: displayControls.controls || desktopUp,
    showControlsButtons,
    showStreamPlayButton,
    displayControls,
    onCloseEvent,
    changeControls,
    changeRightPanel,
    desktopUp,
    usersCount,
    onOpenChat,
    setHasUnreadMessage,
    toggleFullscreen,
    fullscreenRootRef,
  };
}

export default useViewController;
